import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  Search, 
  Download, 
  Eye, 
  Send,
  Book,
  Headphones,
  Video,
  FileText,
  BookOpen,
  Filter,
  Clock,
  Lock,
  CreditCard,
  Star,
  ChevronDown,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const categoryIcons = {
  'book': Book,
  'audio': Headphones, 
  'video': Video,
  'template': FileText,
  'playbook': BookOpen
};

const Marketplace = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showPreview, setShowPreview] = useState(false);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [userCredits, setUserCredits] = useState(475); // Will load from API
  const [showLowCreditsAlert, setShowLowCreditsAlert] = useState(false);

  const filters = [
    { id: 'all', label: 'All Products', count: 0 },
    { id: 'training', label: 'Training', count: 0 },
    { id: 'templates', label: 'Templates', count: 0 },
    { id: 'tools', label: 'Tools', count: 0 },
    { id: 'bonuses', label: 'Bonuses', count: 0 }
  ];

  const levelFilters = [
    { id: 'all', label: 'All Levels' },
    { id: 'invitee', label: 'Invitee' },
    { id: 'rookie', label: 'Rookie' },
    { id: 'rookie_pro', label: 'Rookie Pro' },
    { id: 'professional_builder', label: 'Professional Builder' },
    { id: 'enterprise', label: 'Enterprise' }
  ];

  const sortOptions = [
    { id: 'newest', label: 'Newest First' },
    { id: 'oldest', label: 'Oldest First' },
    { id: 'credits_low', label: 'Credits: Low to High' },
    { id: 'credits_high', label: 'Credits: High to Low' },
    { id: 'popular', label: 'Most Popular' }
  ];

  useEffect(() => {
    loadProducts();
    loadUserCredits();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchQuery, activeFilter, levelFilter, sortBy]);

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      // Updated to use new marketplace catalog API
      const response = await axios.get(`${API_BASE_URL}/api/marketplace/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.items) {
        setProducts(response.data.items);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load marketplace products');
    } finally {
      setLoading(false);
    }
  };

  const loadUserCredits = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_BASE_URL}/api/member/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.credits) {
        setUserCredits(response.data.credits);
        // Show low credits alert if below 100
        setShowLowCreditsAlert(response.data.credits < 100);
      }
    } catch (error) {
      console.error('Error loading user credits:', error);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];
    
    // Apply category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(p => p.category === activeFilter);
    }
    
    // Apply level filter
    if (levelFilter !== 'all') {
      filtered = filtered.filter(p => p.required_level === levelFilter || p.required_level === 'all');
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query) ||
        (p.tags && p.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'credits_low':
          return a.credit_cost - b.credit_cost;
        case 'credits_high':
          return b.credit_cost - a.credit_cost;
        case 'popular':
          return (b.download_count || 0) - (a.download_count || 0);
        default: // newest
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });
    
    setFilteredProducts(filtered);
    
    // Auto-scroll to first result if search is active
    if (searchQuery && filtered.length > 0) {
      setTimeout(() => {
        const firstResult = document.querySelector('.product-card');
        if (firstResult) {
          firstResult.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleRedeemWithCredits = async (product) => {
    if (userCredits < product.credit_cost) {
      toast.error(`Not enough credits! You need ${product.credit_cost} credits but only have ${userCredits}.`);
      setShowLowCreditsAlert(true);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_BASE_URL}/api/marketplace/redeem`, {
        product_id: product.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.ok) {
        toast.success(`${product.title} redeemed successfully!`);
        setUserCredits(prev => prev - product.credit_cost);
        loadProducts(); // Refresh to update product status
      }
    } catch (error) {
      console.error('Error redeeming product:', error);
      toast.error('Failed to redeem product');
    }
  };

  const handleOpenProduct = async (product) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_BASE_URL}/api/marketplace/open`, {
        product_id: product.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
        toast.success('Product opened!');
      }
    } catch (error) {
      console.error('Error opening product:', error);
      toast.error('Failed to open product');
    }
  };

  const getProductStatus = (product) => {
    // Check if user has redeemed this product
    if (product.user_has_access) return 'owned';
    
    // Check if user has enough credits
    if (userCredits < product.credit_cost) return 'locked';
    
    // Check if coming soon
    if (product.release_date && new Date(product.release_date) > new Date()) return 'coming_soon';
    
    return 'available';
  };

  const getStatusButton = (product) => {
    const status = getProductStatus(product);
    
    switch (status) {
      case 'owned':
        return (
          <Button
            onClick={() => handleOpenProduct(product)}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Open/Play/Download
          </Button>
        );
      
      case 'locked':
        return (
          <Button disabled className="w-full bg-gray-300 text-gray-500" title="Insufficient Credits: Top up your credits to unlock this product">
            <Lock className="w-4 h-4 mr-2" />
            Need {product.credit_cost - userCredits} More Credits
          </Button>
        );
      
      case 'coming_soon':
        return (
          <Button disabled className="w-full bg-blue-300 text-blue-700" title="Product releases soon - check back later!">
            <Clock className="w-4 h-4 mr-2" />
            Coming Soon
          </Button>
        );
      
      default: // available
        return (
          <Button
            onClick={() => handleRedeemWithCredits(product)}
            className="w-full bg-yellow-400 text-black hover:bg-yellow-500"
            title="Redeem this product with your credits - instant access!"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Use {product.credit_cost} Credits
          </Button>
        );
    }
  };

  const getProductImage = (product) => {
    // Use product thumbnail if available, otherwise use category-based default
    if (product.thumbnail_url) return product.thumbnail_url;
    
    const cloudinaryBase = 'https://res.cloudinary.com/demo/image/upload/f_auto,q_auto,w_640/v1/samples/';
    const categoryImageMap = {
      'training': `${cloudinaryBase}education/books.jpg`,
      'templates': `${cloudinaryBase}design/template.jpg`,
      'tools': `${cloudinaryBase}tech/laptop.jpg`,
      'bonuses': `${cloudinaryBase}premium/gift.jpg`
    };
    
    return categoryImageMap[product.category] || `${cloudinaryBase}default.jpg`;
  };

  const handlePreview = (product) => {
    setPreviewProduct(product);
    setShowPreview(true);
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'training': Book,
      'templates': FileText,
      'tools': BookOpen,
      'bonuses': Star
    };
    const IconComponent = iconMap[category] || Book;
    return <IconComponent className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Payment Information Disclaimer */}
      <div className="mb-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="text-lg font-bold text-yellow-800 mb-4 flex items-center">
          <span className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-yellow-800 font-bold mr-3">💳</span>
          Payment Information
        </h3>
        <div className="space-y-2 text-sm text-yellow-800">
          <div>• All payments are member-to-member using cryptocurrency</div>
          <div>• Payments go directly to other members' wallets (non-custodial)</div>
          <div>• Matrix system automatically routes payments based on positions</div>
          <div>• Every 5th payment goes to admin wallet for system maintenance</div>
          <div>• Set your wallet address in the Wallet section before purchasing</div>
        </div>
      </div>

      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Marketplace</h1>
        <p className="text-gray-600">
          Download products you can use today. Add items to your Vault or send files directly to the Page Builder.
        </p>
      </div>

      {/* Toolbar */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          {filters.map((filter) => {
            const count = filter.id === 'all' 
              ? products.length 
              : products.filter(p => p.category === filter.id).length;
              
            return (
              <Button
                key={filter.id}
                variant={activeFilter === filter.id ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveFilter(filter.id)}
                className={`${
                  activeFilter === filter.id 
                    ? 'bg-yellow-400 text-black hover:bg-yellow-500' 
                    : 'hover:bg-gray-50'
                }`}
              >
                {filter.label}
                {count > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {count}
                  </Badge>
                )}
              </Button>
            );
          })}
        </div>

        {/* Advanced Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Level Filter */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Level
            </label>
            <select 
              className="w-full p-2 border border-gray-300 rounded-md"
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              {levelFilters.map(level => (
                <option key={level.id} value={level.id}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sort By
            </label>
            <select 
              className="w-full p-2 border border-gray-300 rounded-md"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {sortOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Low Credits Alert */}
      {showLowCreditsAlert && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg relative">
          <Button 
            variant="ghost" 
            size="sm" 
            className="absolute top-2 right-2"
            onClick={() => setShowLowCreditsAlert(false)}
          >
            <X className="w-4 h-4" />
          </Button>
          <div className="flex items-center">
            <CreditCard className="w-5 h-5 text-red-600 mr-2" />
            <div>
              <h4 className="font-semibold text-red-800">Running Low on Credits!</h4>
              <p className="text-sm text-red-700 mt-1">
                You have {userCredits} credits remaining. <Button variant="link" className="p-0 h-auto text-red-700 underline font-semibold">Top up now</Button> to unlock premium products and templates.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map((product) => {
          const status = getProductStatus(product);
          
          return (
            <Card key={product.id} className="product-card overflow-hidden hover:shadow-lg transition-shadow">
              {/* Product Image */}
              <div className="aspect-video bg-gray-100 relative">
                {product.release_date && new Date(product.release_date) > new Date() && (
                  <div className="absolute top-3 right-3 z-10">
                    <Badge className="bg-blue-600 text-white">
                      Coming Soon
                    </Badge>
                  </div>
                )}
                
                <img
                  src={getProductImage(product)}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'w-full h-full bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center';
                    fallback.innerHTML = '<div class="text-4xl text-gray-400">📄</div>';
                    e.target.parentElement.appendChild(fallback);
                  }}
                />
              </div>
              
              <CardContent className="p-4">
                {/* Product Header */}
                <div className="mb-3">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg text-gray-900 line-clamp-2 flex-1">
                      {product.title}
                    </h3>
                    {product.featured && (
                      <Badge variant="outline" className="ml-2 text-xs">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                    {product.description}
                  </p>
                  
                  {/* Product Meta */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    {getCategoryIcon(product.category)}
                    <span className="capitalize">{product.category}</span>
                    <span>•</span>
                    <span>{product.required_level || 'All Levels'}</span>
                    {product.format && (
                      <>
                        <span>•</span>
                        <span className="uppercase">{product.format}</span>
                      </>
                    )}
                  </div>

                  {/* Credit Cost */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <CreditCard className="w-4 h-4 text-yellow-600 mr-1" />
                      <span className="font-bold text-yellow-600">
                        {product.credit_cost} Credits
                      </span>
                    </div>
                    
                    {product.access_duration && (
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {product.access_duration}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  {getStatusButton(product)}
                  
                  {status === 'available' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreview(product)}
                      className="w-full"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {/* Empty State */}
      {filteredProducts.length === 0 && !loading && (
        <div className="text-center py-12">
          <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-600">
            {searchQuery || activeFilter !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Products will appear here as they become available'
            }
          </p>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Preview: {previewProduct.title}</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex-1 p-6 overflow-auto">
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <div className="text-4xl text-gray-400 mb-4">
                  {getCategoryIcon(previewProduct.category)}
                </div>
                <h4 className="text-xl font-medium mb-2">{previewProduct.title}</h4>
                <p className="text-gray-600 mb-4">{previewProduct.description}</p>
                
                {/* What You'll Learn Section */}
                {previewProduct.what_youll_learn && (
                  <div className="mt-6 text-left">
                    <h5 className="font-semibold mb-3">What You'll Learn:</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {previewProduct.what_youll_learn.map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-6 flex items-center justify-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <CreditCard className="w-4 h-4 mr-1" />
                    {previewProduct.credit_cost} Credits
                  </div>
                  {previewProduct.access_duration && (
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      Access: {previewProduct.access_duration}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Close
              </Button>
              {getProductStatus(previewProduct) === 'available' && (
                <Button 
                  onClick={() => {
                    handleRedeemWithCredits(previewProduct);
                    setShowPreview(false);
                  }}
                  className="bg-yellow-400 text-black hover:bg-yellow-500"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Use {previewProduct.credit_cost} Credits
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marketplace;